import type { GetShortQuestionnaireSummaryReportQuery } from '@report/graphql';

import {
  AGE_LABELS,
  buildSection,
  EDUCATION_LABELS,
  ORGANIZATION_LABELS,
  type QuestionnaireReportSection,
  RATING_LABELS,
  type SectionConfig,
} from '../questionnaire-report-shared';

export type ShortFormSection = QuestionnaireReportSection;

type ShortQuestionnaireSummaryReport =
  GetShortQuestionnaireSummaryReportQuery['reportGetShortQuestionnaireSummaryReport'];

export const OBJECTIVE_LABELS: Record<string, string> = {
  'career-opportunity': 'เพื่อสร้างโอกาสด้านอาชีพ / การสมัครงาน',
  'register-license-and-profession': 'เพื่อขึ้นทะเบียนใบอนุญาตฯ / ใบประกอบวิชาชีพ',
  'self-assessment': 'เพื่อประเมินความรู้ตนเอง',
  'special-project-with-outside-institution':
    'สอบตามเงื่อนไขของโครงการเฉพาะของหน่วยงานภายนอก / สถาบันการศึกษา',
  other: 'อื่น ๆ โปรดระบุ',
};

type SectionField = Exclude<keyof ShortQuestionnaireSummaryReport, '__typename'>;

const TOP_SECTION_CONFIGS: SectionConfig<SectionField>[] = [
  { key: 'section-1', variant: 'standard', title: '1 อายุ', field: 'age', labelMap: AGE_LABELS },
  {
    key: 'section-2',
    variant: 'standard',
    title: '2 ระดับการศึกษาสูงสุด',
    field: 'highestEducationLevel',
    labelMap: EDUCATION_LABELS,
  },
  {
    key: 'section-3',
    variant: 'standard',
    title: '3 ประเภทหน่วยงานที่ท่านทำงานอยู่ในปัจจุบัน',
    field: 'currentOrganization',
    labelMap: ORGANIZATION_LABELS,
  },
  {
    key: 'section-4',
    variant: 'standard',
    title: '4 วัตถุประสงค์ของการเข้าสอบ',
    field: 'examAttendingObjectives',
    labelMap: OBJECTIVE_LABELS,
  },
];

const SATISFACTION_SUBSECTION_CONFIGS: SectionConfig<SectionField>[] = [
  {
    key: 'section-5-1',
    variant: 'standard',
    title: '5.1 ด้านสถานที่',
    field: 'locationSatisfaction',
    labelMap: RATING_LABELS,
  },
  {
    key: 'section-5-2',
    variant: 'standard',
    title: '5.2 ด้านการให้บริการของเจ้าหน้าที่',
    field: 'staffServicesSatisfaction',
    labelMap: RATING_LABELS,
  },
  {
    key: 'section-5-3',
    variant: 'standard',
    title: '5.3 ด้านระบบจัดทดสอบ — ความสะดวกและเข้าใจง่ายในการใช้งาน',
    field: 'systemUsabilitySatisfaction',
    labelMap: RATING_LABELS,
  },
  {
    key: 'section-5-4',
    variant: 'standard',
    title: '5.4 ด้านระบบจัดทดสอบ — ความเสถียรและความต่อเนื่องของระบบ',
    field: 'systemStabilitySatisfaction',
    labelMap: RATING_LABELS,
  },
];

/**
 * Builds the ordered Short Form sections. When `report` is undefined (query
 * still loading), a placeholder scaffold is returned so the table stays
 * visible and the loading skeleton can render.
 */
export function buildShortFormSections(
  report?: ShortQuestionnaireSummaryReport,
): ShortFormSection[] {
  return [
    ...TOP_SECTION_CONFIGS.map((config) => buildSection(config, report)),
    {
      key: 'section-5',
      variant: 'rating',
      title: '5 ความพึงพอใจต่อการใช้บริการสอบ',
      rows: [],
      subSections: SATISFACTION_SUBSECTION_CONFIGS.map((config) => buildSection(config, report)),
    },
  ];
}
